(() => {
  'use strict';

  if (customElements.get('rd-random-products')) return;

  const TEN_MINUTES = 10 * 60 * 1000;

  const randomFloat = () => {
    if (window.crypto?.getRandomValues) {
      const values = new Uint32Array(1);
      window.crypto.getRandomValues(values);
      return values[0] / 4294967296;
    }

    return Math.random();
  };

  const shuffle = (items) => {
    const result = items.slice();

    for (let index = result.length - 1; index > 0; index -= 1) {
      const swapIndex = Math.floor(randomFloat() * (index + 1));
      [result[index], result[swapIndex]] = [result[swapIndex], result[index]];
    }

    return result;
  };

  class RDRandomProducts extends HTMLElement {
    connectedCallback() {
      if (this.dataset.rdRandomReady === 'true') return;
      this.dataset.rdRandomReady = 'true';

      this.target = this.querySelector('[data-rd-random-target]');
      this.template = this.querySelector('template[data-rd-random-template]');
      this.limit = Math.max(1, Number.parseInt(this.dataset.rdRandomLimit || '4', 10) || 4);
      this.timer = 0;
      this.lastSignature = '';

      if (!this.target || !(this.template instanceof HTMLTemplateElement)) return;

      // Shuffle immediately on every page load / section render.
      // Do NOT use a deterministic time seed: that made repeated reloads within
      // the same 10-minute window show the exact same products.
      this.render();
      this.startRotation();
    }

    disconnectedCallback() {
      if (this.timer) {
        window.clearInterval(this.timer);
        this.timer = 0;
      }
    }

    getPool() {
      return Array.from(this.template.content.children).filter(
        (node) => node instanceof HTMLElement && node.hasAttribute('data-rd-random-item')
      );
    }

    getItemKey(item, fallbackIndex) {
      const explicitId = item.getAttribute('data-rd-random-product-id');
      if (explicitId) return explicitId;

      const link = item.querySelector('a[href]');
      if (link?.getAttribute('href')) return link.getAttribute('href');

      return String(fallbackIndex);
    }

    getSignature(items) {
      return items
        .map((item, index) => this.getItemKey(item, index))
        .join('|');
    }

    choose(pool) {
      const take = Math.min(this.limit, pool.length);
      if (take <= 0) return [];

      let selected = shuffle(pool).slice(0, take);
      let signature = this.getSignature(selected);

      // Avoid an identical visible result on the next 10-minute rotation when
      // there is enough choice in the collection.
      if (pool.length > 1 && signature === this.lastSignature) {
        for (let attempt = 0; attempt < 7; attempt += 1) {
          selected = shuffle(pool).slice(0, take);
          signature = this.getSignature(selected);

          if (signature !== this.lastSignature) break;
        }
      }

      // Absolute fallback for very small pools: rotate the shuffled array so
      // the visible order still changes where possible.
      if (pool.length > 1 && signature === this.lastSignature) {
        const currentKeys = this.lastSignature.split('|');
        const alternative = pool.slice();
        const firstCurrentKey = currentKeys[0];
        const index = alternative.findIndex(
          (item, itemIndex) => this.getItemKey(item, itemIndex) !== firstCurrentKey
        );

        if (index > 0) {
          selected = alternative.slice(index).concat(alternative.slice(0, index)).slice(0, take);
          signature = this.getSignature(selected);
        }
      }

      this.lastSignature = signature;
      return selected;
    }

    render() {
      const pool = this.getPool();
      if (!pool.length) return;

      const selected = this.choose(pool);
      const fragment = document.createDocumentFragment();

      selected.forEach((item) => {
        fragment.appendChild(item.cloneNode(true));
      });

      this.target.replaceChildren(fragment);
      this.dataset.rdRandomLastChanged = String(Date.now());
    }

    startRotation() {
      if (this.timer) window.clearInterval(this.timer);

      this.timer = window.setInterval(() => {
        this.render();
      }, TEN_MINUTES);
    }
  }

  customElements.define('rd-random-products', RDRandomProducts);
})();
